/**
 * Dialog para gerar link de agendamento vinculado ao deal
 * 
 * Lista os event types ativos do tenant e, ao selecionar,
 * gera o link público com ?deal_id=DEAL_UUID e copia para clipboard.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarClock, Clock, Copy, Check, ExternalLink } from 'lucide-react';
import { useSchedulingEventTypes } from '@/hooks/useSchedulingEventTypes';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { stripTemplateVariables } from '@/lib/schedulingUtils';

interface DealSchedulingLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  dealTitle: string;
}

export function DealSchedulingLinkDialog({
  open,
  onOpenChange,
  dealId,
  dealTitle,
}: DealSchedulingLinkDialogProps) {
  const { eventTypes, isLoading } = useSchedulingEventTypes();
  const { currentTenant } = useAuth();
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  // Filtra apenas event types ativos
  const activeEventTypes = eventTypes?.filter((et) => et.is_active) || [];

  /** Gera link e copia para clipboard (inclui slug do tenant para unicidade) */
  const handleCopyLink = async (slug: string) => {
    const baseUrl = window.location.origin;
    const tenantSlug = currentTenant?.slug || 'empresa';
    const link = `${baseUrl}/schedule/${tenantSlug}/${slug}?deal_id=${dealId}`;

    try {
      await navigator.clipboard.writeText(link);
      setCopiedSlug(slug);
      toast.success('Link copiado para a área de transferência!');

      // Resetar ícone após 2s
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-5 w-5 text-primary" />
            Gerar link de agendamento
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Selecione o tipo de reunião para o negócio <strong className="text-foreground">{dealTitle}</strong>
          </p>
        </DialogHeader>

        <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto">
          {/* Carregando */}
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          )}

          {/* Sem event types ativos */}
          {!isLoading && activeEventTypes.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nenhum tipo de evento ativo.</p>
              <p className="text-xs mt-1">Configure seus tipos de evento em Configurações → Agendamento.</p>
            </div>
          )}

          {/* Lista de event types */}
          {activeEventTypes.map((et) => (
            <div
              key={et.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-accent/50 transition-colors group"
            >
              {/* Indicador de cor */}
              <div
                className="h-8 w-1 rounded-full shrink-0"
                style={{ backgroundColor: et.color || '#3B82F6' }}
              />

              {/* Info do event type */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{stripTemplateVariables(et.title)}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {et.duration_minutes} min
                </p>
              </div>

              {/* Botão copiar link */}
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1.5 text-xs"
                onClick={() => handleCopyLink(et.slug)}
              >
                {copiedSlug === et.slug ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copiar link
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
